import React, { lazy, Suspense } from "react";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Routes, Route, Navigate, Outlet, Link } from "react-router-dom";
import Login from "./components/login/Login";
import Register from "./components/register/Register";
import ProtectedRoute from "./components/ProtectedRoute";
import ServiceProtectedRoute from "./components/ServiceProtectedRoute";
import "./App.css";
import Sidebar from "./components/sidebar/sidebar";
import { QueryProvider } from "./providers/QueryProvider";
import VerificationPending from "./components/pages/VerificationPending";
import AccountRejected from "./components/pages/AccountRejected";

// Import page components
import Dashboard from "./components/pages/Dashboard";
import Package from "./components/pages/package/Package";
import AddPackagePage from "./components/pages/package/AddPackagePage";
import Booking from "./components/pages/Booking";
import Transactions from "./components/pages/Transactions";
import Enquiry from "./components/pages/Enquiry";
import Hotels from "./components/pages/Hotels";
import Attractions from "./components/pages/attractions/Attractions";
import PackageAttractions from "./components/pages/PackageAttractions";
import Activities from "./components/pages/activity/Activities";
import Rentals from "./components/pages/rental/Rentals";
import RentalCategory from "./components/pages/rental/RentalCategory";
import RentalSubCategory from "./components/pages/rental/RentalSubCategory";
import RentalManagement from "./components/pages/rental/RentalManagement";
import Slots from "./components/pages/slots/Slots";
import StopSales from "./components/pages/stopsales/StopSales";
import Events from "./components/pages/events/Events";
import Profile from "./components/pages/Profile";

const AddEventPage = lazy(() =>
  import("./components/pages/events/AddEventPage")
);
const AddAttractionPage = lazy(() =>
  import("./components/pages/attractions/AddAttractionPage")
);

const PageLoader = () => (
  <div className="flex min-h-[40vh] items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

// Add this new ProtectedLayout component
const ProtectedLayout = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <main className="relative z-0 min-h-screen min-w-0 transform-gpu p-4 lg:ml-64">
        <Outlet />
      </main>
    </div>
  );
};

function App() {
  return (
    <QueryProvider>
      <DndProvider backend={HTML5Backend}>
        <div className="App">
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verification-pending" element={<VerificationPending />} />
            <Route path="/account-rejected" element={<AccountRejected />} />

            {/* Protected routes with sidebar */}
            <Route
              element={
                <ProtectedRoute>
                  <ProtectedLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/Dashboard" element={<Dashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />

              {/* Package service routes */}
              <Route
                path="/packages"
                element={
                  <ServiceProtectedRoute requiredService="package">
                    <Package />
                  </ServiceProtectedRoute>
                }
              />
              <Route
                path="/packages/new"
                element={
                  <ServiceProtectedRoute requiredService="package">
                    <AddPackagePage />
                  </ServiceProtectedRoute>
                }
              />
              <Route
                path="/packages/:packageId/edit"
                element={
                  <ServiceProtectedRoute requiredService="package">
                    <AddPackagePage />
                  </ServiceProtectedRoute>
                }
              />
              <Route
                path="/slots"
                element={
                  <ServiceProtectedRoute requiredService="package">
                    <Slots />
                  </ServiceProtectedRoute>
                }
              />
              <Route
                path="/stop-sales"
                element={
                  <ServiceProtectedRoute requiredService="package">
                    <StopSales />
                  </ServiceProtectedRoute>
                }
              />
              {/* Event service routes */}
              <Route
                path="/events"
                element={
                  <ServiceProtectedRoute requiredService="event">
                    <Events />
                  </ServiceProtectedRoute>
                }
              />
              <Route
                path="/events/new"
                element={
                  <ServiceProtectedRoute requiredService="event">
                    <Suspense fallback={<PageLoader />}>
                      <AddEventPage />
                    </Suspense>
                  </ServiceProtectedRoute>
                }
              />
              <Route
                path="/events/:eventId/edit"
                element={
                  <ServiceProtectedRoute requiredService="event">
                    <Suspense fallback={<PageLoader />}>
                      <AddEventPage />
                    </Suspense>
                  </ServiceProtectedRoute>
                }
              />

              {/* Activity service routes */}
              <Route
                path="/activities"
                element={
                  <ServiceProtectedRoute requiredService="activity">
                    <Activities />
                  </ServiceProtectedRoute>
                }
              />

              {/* Attraction service routes */}
              <Route
                path="/Attractions"
                element={
                  <ServiceProtectedRoute requiredService="attraction">
                    <Attractions />
                  </ServiceProtectedRoute>
                }
              />
              <Route
                path="/attractions/new"
                element={
                  <ServiceProtectedRoute requiredService="attraction">
                    <Suspense fallback={<PageLoader />}>
                      <AddAttractionPage />
                    </Suspense>
                  </ServiceProtectedRoute>
                }
              />
              <Route
                path="/attractions/:attractionId/edit"
                element={
                  <ServiceProtectedRoute requiredService="attraction">
                    <Suspense fallback={<PageLoader />}>
                      <AddAttractionPage />
                    </Suspense>
                  </ServiceProtectedRoute>
                }
              />
              <Route
                path="/master-attractions"
                element={
                  <ServiceProtectedRoute requiredService="attraction">
                    <PackageAttractions />
                  </ServiceProtectedRoute>
                }
              />

              {/* Rental service routes */}
              <Route
                path="/rentals"
                element={
                  <ServiceProtectedRoute requiredService="rental">
                    <Rentals />
                  </ServiceProtectedRoute>
                }
              />
              <Route
                path="/rentals/management"
                element={
                  <ServiceProtectedRoute requiredService="rental">
                    <RentalManagement />
                  </ServiceProtectedRoute>
                }
              />
              <Route
                path="/rental-category"
                element={
                  <ServiceProtectedRoute requiredService="rental">
                    <RentalCategory />
                  </ServiceProtectedRoute>
                }
              />
              <Route
                path="/rental-sub-category"
                element={
                  <ServiceProtectedRoute requiredService="rental">
                    <RentalSubCategory />
                  </ServiceProtectedRoute>
                }
              />

              {/* Hotel routes - available for package or rental */}
              <Route
                path="/Hotels"
                element={
                  <ServiceProtectedRoute requiredService={["package", "rental"]}>
                    <Hotels />
                  </ServiceProtectedRoute>
                }
              />

              {/* Always accessible routes */}
              <Route path="/booking" element={<Booking />} />
              <Route path="/transactions" element={<Transactions />} />
              <Route path="/enquiry" element={<Enquiry />} />
              <Route path="/profile" element={<Profile />} />

            </Route>

            {/* Redirect root to dashboard or login */}
            <Route path="/" element={<Navigate to="/Dashboard" replace />} />

            {/* Catch all route - 404 */}
            <Route
              path="*"
              element={
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center">
                    <h1 className="text-4xl font-bold text-gray-800">404</h1>
                    <p className="text-gray-600 mt-2">Page not found</p>
                    <Link
                      to="/Dashboard"
                      className="mt-4 inline-block px-4 py-2 bg-pink-600 text-white rounded-md"
                    >
                      Go to Dashboard
                    </Link>
                  </div>
                </div>
              }
            />
          </Routes>
        </div>
      </DndProvider>
    </QueryProvider>
  );
}

export default App;
